import React, { useState, useEffect, memo } from 'react';
import { connect, useSelector } from 'react-redux';
import _ from 'lodash';
import { FormUp, FormDown } from 'grommet-icons';
import { CryptoIcon } from 'components/CryptoIcon';
import { CoinTracker } from 'containers/CoinTracker';
import { usePopper, Tooltip, Arrow } from 'components/Tooltip/Popper';
import { Box, Text } from 'components/Wrapped';
import { formatNumberToPlaces, useOuterClickNotifier } from 'utils';
import { PrettyNumberTZ, PrettyPercent } from '../../components/Helpers';
import { getSingleTradingPairSettings } from '../../redux/selectors/settings';
import { getPricesByTradingPair } from 'redux/selectors/markets';

const currencySign = {
  USDT: '$',
  RUB: '₽',
};

const CoinTrackerPopper = memo((props) => {
  const { coinData, size, quoteCurrency, baseCurrency, isLoading, tickDecimals } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { reference, popper } = usePopper({
    placement: 'bottom-start',
  });

  useEffect(() => {
    setIsOpen(false);
  }, [baseCurrency, quoteCurrency]);

  useOuterClickNotifier(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  }, [reference, popper]);

  return (
    <React.Fragment>
      <Box onClick={() => setIsOpen(!isOpen)} forwardRef={reference} pad="none">
        <Box
          pad={{ horizontal: '12px', vertical: '8px' }}
          background="rgba(255, 255, 255, 0.1)"
          round="1000px"
          direction="row"
          gap="8px"
          justify="center"
          align="center"
          style={{ backdropFilter: 'blur(120px)' }}
        >
          <CryptoIcon currency={baseCurrency} margin="none" />
          <Text size="14px" weight={600}>
            {baseCurrency}/{quoteCurrency}
          </Text>
          {isOpen ? <FormUp size="16px" color="white" /> : <FormDown size="16px" color="white" />}
        </Box>
        {size === 'small' && (
          <div style={{ marginTop: '10px', marginLeft: '11px' }}>
            <Box pad="none" justify={size === 'small' ? 'between' : 'center'} gap="2px">
              <Text color="#B4B6B7" size="14px" weight={500} style={{ lineHeight: '16px' }}>
                Последняя цена
              </Text>
              <Text color="white" size="25px" weight={600} style={{ lineHeight: '17px' }} textAlign="start">
                <Box direction="row" pad="none" style={{ marginTop: '8px' }}>
                  {currencySign[coinData.quoteCurrency]}
                  <PrettyNumberTZ number={formatNumberToPlaces(coinData.lastPrice, tickDecimals)} style={{ fontSize: '25px' }} />
                </Box>
              </Text>
            </Box>
            <Box pad="none" justify={size === 'small' ? 'between' : 'center'} gap="2px">
              <Text color="white" size="14px" weight={600} style={{ lineHeight: '17px' }} textAlign="start">
                <Box pad="none" direction="row" style={{ marginTop: '13px' }}>
                  <PrettyPercent value={coinData.priceChange24h} sign="" />
                </Box>
              </Text>
            </Box>
          </div>
        )}
      </Box>
      <Tooltip forwardRef={popper} hidden={isLoading || !isOpen} arrowHidden background="transparent" padding="0px" width={{ max: '100%' }}>
        <Box height={{ max: '500px' }} pad="none">
          {!isLoading && isOpen && (
            <CoinTracker background="background-3" currentQuoteCurrency={`${quoteCurrency}`} currentBaseCurrency={`${baseCurrency}`} />
          )}
        </Box>
        <Arrow background="background-3" data-popper-arrow />
      </Tooltip>
    </React.Fragment>
  );
});

const NavCoinPicker = ({ isLoading, size, tradingPairSettings: { tickDecimals }, tradingPair: { quoteCurrency, baseCurrency } }) => {
  const pricesByTradingPair = useSelector(getPricesByTradingPair);
  const [coinData, setCoinData] = useState(pricesByTradingPair);

  // todo: research времеменное решение
  useEffect(() => {
    const shouldUpdate = JSON.stringify(coinData) !== JSON.stringify(pricesByTradingPair);

    if (shouldUpdate) {
      setCoinData(pricesByTradingPair);
    }
  }, [coinData, pricesByTradingPair]);

  if (_.isEmpty(coinData)) {
    return null;
  }

  return (
    <CoinTrackerPopper
      quoteCurrency={quoteCurrency}
      baseCurrency={baseCurrency}
      tickDecimals={tickDecimals}
      isLoading={isLoading}
      coinData={coinData}
      size={size}
    />
  );
};

const mapStateToProps = (state) => {
  const {
    exchange,
    exchangeSettings: { isSettingsLoading, isCurrencySettingsLoading },
  } = state;

  return {
    tradingPair: exchange.tradingPair,
    tradingPairSettings: getSingleTradingPairSettings(state, {
      tradingPair: state.exchange.tradingPair,
    }),
    isLoading: isSettingsLoading || isCurrencySettingsLoading,
  };
};

export default connect(mapStateToProps)(NavCoinPicker);
